import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getEvaluationPublique, soumettreEvaluation } from '../services/evaluationPubliqueService';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import './Signature.css';
import '../assets/styles/Evaluation.css';

/** Le résultat de la personne : sa note sur le nombre de questions et sa réussite. */
function Resultat({ resultat }) {
  if (!resultat) return null;
  const pct = Math.round(Number(resultat.pourcentageReussite ?? 0));
  return (
    <div className="evaluation-resultat">
      <div className="evaluation-tuile">
        <div className="evaluation-tuile-valeur">
          {resultat.nbReussies} / {resultat.nbQuestions}
        </div>
        <div className="evaluation-tuile-libelle">
          question{resultat.nbQuestions > 1 ? 's' : ''} réussie{resultat.nbReussies > 1 ? 's' : ''}
        </div>
      </div>
      <div className="evaluation-tuile">
        <div className="evaluation-tuile-valeur">{pct} %</div>
        <div className="evaluation-tuile-libelle">de réussite</div>
        <div className="evaluation-tuile-detail">
          {resultat.pointsObtenus} / {resultat.pointsMax} points
        </div>
      </div>
    </div>
  );
}

/**
 * Évaluation de fin de formation, atteinte par le lien reçu par email.
 *
 * Le lien désigne le stagiaire et la version du QCM de la formation suivie.
 * Les questions arrivent sans barème ; la note se calcule côté serveur et ne
 * s'affiche qu'à la fin. Un lien ne sert qu'une fois : rouvert après coup, il
 * ne montre plus que le résultat.
 */
function EvaluationPublique() {
  const { token } = useParams();
  const [donnees, setDonnees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // questionId -> tableau d'identifiants d'options cochées
  const [reponses, setReponses] = useState({});
  // code de question commune -> valeur saisie
  const [infos, setInfos] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState(null);
  const [resultat, setResultat] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getEvaluationPublique(token)
      .then((res) => {
        const d = res.data;
        setDonnees(d);
        if (d.termine) setResultat(d.resultat);
        setInfos(Object.fromEntries((d.champs || []).map((c) => [c.code, c.valeur || ''])));
      })
      .catch((err) => setError(err.message || 'Lien invalide.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    charger();
  }, [charger]);

  const questions = useMemo(() => donnees?.questions || [], [donnees]);
  const nbRepondues = questions.filter((q) => (reponses[q.id] || []).length > 0).length;

  function choisir(question, optionId) {
    setReponses((prev) => {
      const courantes = prev[question.id] || [];
      if (question.type === 'qcm') return { ...prev, [question.id]: [optionId] };
      return {
        ...prev,
        [question.id]: courantes.includes(optionId)
          ? courantes.filter((id) => id !== optionId)
          : [...courantes, optionId],
      };
    });
  }

  async function valider(e) {
    e.preventDefault();
    setErreurEnvoi(null);

    // Les questions communes obligatoires, avant tout envoi.
    for (const c of donnees.champs || []) {
      if (c.obligatoire && !String(infos[c.code] || '').trim()) {
        setErreurEnvoi(`Merci de renseigner : « ${c.libelle} ».`);
        return;
      }
    }

    const sansReponse = questions.length - nbRepondues;
    if (
      sansReponse > 0 &&
      !window.confirm(
        `${sansReponse} question${sansReponse > 1 ? 's' : ''} sans réponse : elle${
          sansReponse > 1 ? 's' : ''
        } compter${sansReponse > 1 ? 'ont' : 'a'} pour zéro. Valider quand même ?`
      )
    ) {
      return;
    }

    setEnvoi(true);
    try {
      const res = await soumettreEvaluation(token, {
        infos,
        reponses: questions.map((q) => ({ questionId: q.id, optionIds: reponses[q.id] || [] })),
      });
      setResultat(res.data.resultat);
      setDonnees((d) => ({ ...d, termine: true }));
      window.scrollTo?.({ top: 0 });
    } catch (err) {
      setErreurEnvoi(err.message || 'Vos réponses n’ont pas pu être enregistrées.');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="devis-page devis-page-scroll signature-doc evaluation">
      <header className="emargement-entete">
        <h1 className="devis-title">Évaluation de fin de formation</h1>
        <p className="devis-subtitle">
          {donnees ? donnees.formation || donnees.evaluation : 'Chargement…'}
        </p>
      </header>

      {loading && <div className="signature-etat">Chargement de votre évaluation...</div>}
      {!loading && error && <div className="signature-etat signature-etat-erreur">{error}</div>}

      {!loading && !error && donnees && donnees.termine && (
        <>
          <div className="signature-etat signature-etat-succes emargement-succes">
            <p className="emargement-succes-titre">Merci, votre évaluation est enregistrée !</p>
            <p className="emargement-succes-detail">Voici votre résultat. Vous pouvez fermer cette page.</p>
          </div>
          <Resultat resultat={resultat} />
        </>
      )}

      {!loading && !error && donnees && !donnees.termine && (
        <form onSubmit={valider}>
          <div className="devis-cartes">
            <div className="devis-carte">
              <div className="devis-carte-titre">FORMATION</div>
              <dl>
                <dt>Intitulé</dt>
                <dd>{donnees.session.intitule}</dd>
                <dt>Dates</dt>
                <dd>{donnees.session.dates}</dd>
              </dl>
            </div>
          </div>

          <section className="evaluation-identite">
            <div className="devis-form-section-title">Vous</div>
            {(donnees.champs || []).map((c) => (
              <label className="signature-champ signature-champ-large" key={c.code}>
                <span className="signature-champ-label">
                  {c.libelle}
                  {c.obligatoire ? ' *' : ''}
                </span>
                <input
                  type={c.nature === 'email' ? 'email' : 'text'}
                  className="signature-champ-input"
                  value={infos[c.code] || ''}
                  onChange={(e) => setInfos((prev) => ({ ...prev, [c.code]: e.target.value }))}
                  maxLength={300}
                  disabled={envoi}
                  autoComplete={c.nature === 'email' ? 'email' : 'off'}
                />
              </label>
            ))}
          </section>

          <div className="evaluation-progres" role="status">
            <div
              className="evaluation-progres-barre"
              style={{ width: `${questions.length ? (nbRepondues / questions.length) * 100 : 0}%` }}
            />
            <span className="evaluation-progres-texte">
              {nbRepondues} / {questions.length} question{questions.length > 1 ? 's' : ''} répondue
              {nbRepondues > 1 ? 's' : ''}
            </span>
          </div>

          <ol className="evaluation-questions">
            {questions.map((q, i) => (
              <li className="evaluation-question" key={q.id}>
                <div className="evaluation-question-tete">
                  <span className="evaluation-question-numero">{i + 1}</span>
                  <div>
                    <div className="evaluation-question-enonce">{q.enonce}</div>
                    <div className="evaluation-question-indice">
                      {q.type === 'qcm_multiple'
                        ? 'Plusieurs réponses possibles'
                        : 'Une seule réponse'}
                    </div>
                  </div>
                </div>
                <div className="evaluation-options" role="group">
                  {q.options.map((o) => {
                    const coche = (reponses[q.id] || []).includes(o.id);
                    return (
                      <label
                        key={o.id}
                        className={`evaluation-option${coche ? ' evaluation-option-choisie' : ''}`}
                      >
                        <input
                          type={q.type === 'qcm_multiple' ? 'checkbox' : 'radio'}
                          name={`question-${q.id}`}
                          checked={coche}
                          onChange={() => choisir(q, o.id)}
                          disabled={envoi}
                        />
                        <span>{o.libelle}</span>
                      </label>
                    );
                  })}
                </div>
              </li>
            ))}
          </ol>

          {erreurEnvoi && <div className="signature-etat signature-etat-erreur">{erreurEnvoi}</div>}
          <div className="evaluation-validation">
            <button type="submit" className="devis-btn devis-btn-primary" disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Valider mes réponses'}
            </button>
            <p className="evaluation-note">
              Ce lien vous est personnel et ne sert qu’une fois : vous verrez votre note dès la
              validation.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}

export default EvaluationPublique;
